// START IMPORTS
// Theme and UI imports
import { useTheme } from '@ltvco/refresh-lib/theme';
// Core lib v1 imports
import { Owner } from '@ltvco/refresh-lib/v1';
// Vendor imports
import {
  Email,
  Radar,
  Person2,
  Phone,
  Work,
  TaskAlt,
} from '@mui/icons-material';
// END IMPORTS

// START HELPER_FUNCTIONS
export const getEmailNavLinkData = (
  potentialOwnersLength: number,
  owner: Owner
) => {
  return [
    {
      href: '#email-overview',
      sectionId: '#email-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
      name: 'email-overview',
    },
    {
      href: '#email-fraud-check-section',
      sectionId: '#email-fraud-check-section"',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'fraud_check',
      name: 'email-fraud-check-section',
    },
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: potentialOwnersLength,
      notificationSection: 'owners',
      name: 'potential-owners-section',
    },
    {
      href: '#possible-photos-section',
      sectionId: '#possible-photos-section',
      text: 'Possible Photos',
      reportItemsCount: owner.images?.length || null,
      notificationSection: 'photos',
      name: 'possible-photos-section',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Phone Numbers',
      reportItemsCount: owner.phones.length,
      notificationSection: 'phones',
      name: 'phone-numbers-section',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Emails',
      reportItemsCount: owner.emails.length,
      notificationSection: 'emails',
      name: 'email-section',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: 'Address History',
      reportItemsCount: owner.addresses.length,
      notificationSection: 'addresses',
      name: 'address-history-section',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Professional Background',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
      name: 'jobs-section',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Education',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
      name: 'education-section',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social & Websites',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
      name: 'social-media-section',
    },
  ];
};

export const getEmailNavLinkDataV2 = (
  potentialOwnersLength: number,
  owner: Owner
) => {
  const theme = useTheme();
  return [
    {
      href: '#email-overview',
      labelText: 'Overview',
      name: 'email-overview',
      icons: [
        <Email
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
        />,
      ],
      notificationSection: 'identity',
    },
    {
      href: '#email-fraud-check-section',
      labelText: 'FraudScan Summary',
      icons: [
        <Radar
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      hideReportItemsCount: true,
      name: 'email-fraud-check-section',
      notificationSection: 'fraud_check',
    },
    {
      labelText: 'Potential Owners',
      name: 'potential-owners-section',
      icons: [
        <Person2
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#potential-owners-section',
          labelText: 'Potential Owners',
          reportItemsCount: potentialOwnersLength,
          name: 'potential-owners-section',
          notificationSection: 'owners',
        },
      ],
    },
    {
      labelText: 'Contact Information',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: Math.max(owner.phones.length - 1, 0),
          name: 'phone-numbers-section',
          notificationSection: 'phones',
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: owner.emails.length,
          name: 'email-section',
          notificationSection: 'emails',
        },
        {
          href: '#address-history-section',
          labelText: 'Address History',
          reportItemsCount: owner.addresses.length,
          name: 'address-history-section',
          notificationSection: 'addresses',
        },
        {
          href: '#social-media-section',
          sectionId: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount: owner.profiles.length + owner.usernames.length,
          name: 'social-media-section',
          notificationSection: 'social',
        },
      ],
    },
    {
      labelText: 'Jobs & Education',
      name: 'jobs-educations-section',
      icons: [
        <Work sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }} />,
      ],
      childItems: [
        {
          href: '#jobs-section',
          sectionId: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: owner.jobs.length,
          name: 'jobs-section',
          notificationSection: 'jobs',
        },
        {
          href: '#education-section',
          sectionId: '#education-section',
          labelText: 'Education',
          reportItemsCount: owner.educations.length,
          name: 'education-section',
          notificationSection: 'educations',
        },
      ],
    },
    {
      labelText: 'Next Steps',
      name: 'next-steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};
// END HELPER_FUNCTIONS
